"use client";

import type { KeyboardEvent } from "react";
import type { JEOPARDY_GAME_MAKER_STRINGS } from "@/components/tools/jeopardy-game-maker";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type Category,
  cellKey,
} from "@/lib/tool-kit/games-organizers/jeopardy";

type Strings = typeof JEOPARDY_GAME_MAKER_STRINGS;

const ARROWS: Record<string, [number, number]> = {
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
};

/** Move focus to the nearest playable cell in the arrow's direction. */
function moveFocus(
  event: KeyboardEvent<HTMLButtonElement>,
  column: number,
  row: number,
  columns: number,
  rows: number
): void {
  const step = ARROWS[event.key];
  if (!step) {
    return;
  }
  event.preventDefault();
  let c = column + step[0];
  let r = row + step[1];
  while (c >= 0 && c < columns && r >= 0 && r < rows) {
    const next = event.currentTarget
      .closest("table")
      ?.querySelector<HTMLButtonElement>(
        `[data-cell="${cellKey(c, r)}"]:not(:disabled)`
      );
    if (next) {
      next.focus();
      return;
    }
    c += step[0];
    r += step[1];
  }
}

/** The board: categories across the top, one button per clue, arrow keys to move about. */
export default function JeopardyBoard({
  categories,
  rows,
  used,
  strings,
  onOpen,
}: {
  categories: readonly Category[];
  rows: number;
  used: ReadonlySet<string>;
  strings: Strings;
  onOpen: (column: number, row: number) => void;
}) {
  const fmt = useToolNumber();
  return (
    <div className="overflow-x-auto">
      <table
        aria-label={strings.boardAria}
        className="w-full table-fixed border-separate border-spacing-1.5 sm:border-spacing-2"
      >
        <thead>
          <tr>
            {categories.map((category, column) => (
              <th
                className="rounded-md bg-primary px-1 py-3 text-center font-bold text-primary-foreground text-xs uppercase leading-tight sm:px-2 sm:py-4 sm:text-base"
                // biome-ignore lint/suspicious/noArrayIndexKey: category names may repeat
                key={column}
                scope="col"
              >
                {category.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, row) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional
            <tr key={row}>
              {categories.map((category, column) => {
                const clue = category.clues[row];
                const key = cellKey(column, row);
                if (!clue) {
                  return <td key={key} />;
                }
                const done = used.has(key);
                const values = {
                  category: category.name,
                  points: fmt(clue.points),
                };
                return (
                  <td className="p-0" key={key}>
                    <button
                      aria-label={t(
                        done ? strings.cellDone : strings.cellLabel,
                        values
                      )}
                      className="h-16 w-full rounded-md border border-border bg-card font-bold text-foreground text-xl transition-colors hover:bg-primary/15 focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:bg-muted disabled:text-transparent sm:h-24 sm:text-4xl"
                      data-cell={key}
                      disabled={done}
                      onClick={() => onOpen(column, row)}
                      onKeyDown={(event) =>
                        moveFocus(event, column, row, categories.length, rows)
                      }
                      type="button"
                    >
                      {fmt(clue.points)}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
